import Axios from 'axios'
import env from '../../config/env'
import { Role } from '../../utils/constants/Roles'
import { fetcher } from '../../utils/helpers'
import TenantService, { TenantServicePath } from './TenantService'
import {
  UpdateOnboardingRequestBodyType,
  LoginRequestBodyType,
  OtpVerifyRequestBodyType,
  SignUpRequestBodyType,
  PolciyRequestBodyType,
  UpdateCurrentUserRequestBodyType,
  InviteUserRequestBodyType,
  UpdateCurrentTenantInvoiceRequestBodyType,
  SignUpV2RequestBodyType,
  AccountAvailabilityRequestBodyType,
  AuthenticationCredentialRequestBodyType,
  ForgotPasswordRequestBodyType,
  ConfirmSignupRequestBodyType,
  AuthenticationMethodRequestBodyType,
  ConfirmForgotPasswordRequestBody,
  ResendEmailConfirmationRequestBody,
} from './TenantService.types'

export const GetCurrentUserFetcher = (url: string) =>
  fetcher({
    baseURL: env.tenantUrl,
    method: 'GET',
    url,
  })

export const GetUsersFetcher = ([url, query]: string[]) =>
  fetcher({
    baseURL: env.tenantUrl,
    method: 'GET',
    url: `${url}${query}`,
  })

export const GetAccountAvailabilityFetcher = (data: AccountAvailabilityRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'GET',
    url: `${TenantServicePath.GetAccountAvailability}?authenticationMethod=${
      data?.authenticationMethod
    }&authenticationCredential=${encodeURIComponent?.(data.authenticationCredential || '')}`,
  })

export const PolicyFetcher = (data: PolciyRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.Policy,
  })

export const LoginFetcher = (data: LoginRequestBodyType) => {
  return fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.Login,
  })
}

export const OtpVerifyFetcher = (data: OtpVerifyRequestBodyType) => {
  return fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.OtpVerify,
  })
}

export const SignUpFetcher = (data: SignUpRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.SignUp,
  })

export const SignUpV2Fetcher = (data: SignUpV2RequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.SignUpV2,
  })

export const UpdateCurrentUserFetcher = (data: Partial<UpdateCurrentUserRequestBodyType>) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'PUT',
    url: TenantServicePath.Users,
  })

export const UpdateOnboardingFetcher = ([url, data]: [string, UpdateOnboardingRequestBodyType]) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'PUT',
    url,
  })

export const InviteUserFetcher = (data: InviteUserRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: `${TenantServicePath.InviteUser}${data?.role === Role.TENANT_ADMIN ? '?nonDriver=false' : ''}`,
  })

export const CreateRefreshToken = async () =>
  await fetch(`${env.tenantUrl}${TenantServicePath.RefreshToken}`, {
    method: 'POST',
    headers: {
      'x-api-key': env.APIKey,
    },
    body: JSON.stringify({
      refreshToken: localStorage.getItem('yld_r_token') as string,
    }),
  })

export const GetUserVehicles = (params: [string, string], query: string) => {
  return fetcher({
    baseURL: env.tenantUrl,
    method: 'GET',
    url: `${params?.[0]}${params?.[1]}`,
  })
}

export const UpdateCurrentTenantInvoiceFetcher = (data: UpdateCurrentTenantInvoiceRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'PUT',
    url: TenantServicePath.Invoice,
  })

export const AuthenticationCredentialFetcher = (data: AuthenticationCredentialRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'PUT',
    url: TenantServicePath.AuthenticationCredential,
  })

export const ForgotPasswordFetcher = (data: ForgotPasswordRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.ForgotPassword,
  })

export const ConfirmSignUpFetcher = (data: ConfirmSignupRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.ConfirmSignup,
  })

export const AuthenticationMethodFetcher = (data: AuthenticationMethodRequestBodyType) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'PUT',
    url: TenantServicePath.AuthenticationMethod,
  })

export const ConfirmForgotPasswordFetcher = (data: ConfirmForgotPasswordRequestBody) =>
  fetcher({ baseURL: env.tenantUrl, data, method: 'POST', url: TenantServicePath.ConfirmForgotPassword })

export const ResendEmailConfirmationFetcher = (data: ResendEmailConfirmationRequestBody) =>
  fetcher({
    baseURL: env.tenantUrl,
    data,
    method: 'POST',
    url: TenantServicePath.ResendEmailConfirmation,
  })

export const GetCompanyDocumentPreSignedUrlFetcher = (query: string) => {
  return fetcher({
    baseURL: env.tenantUrl,
    method: 'GET',
    url: `${TenantServicePath.GetCompanyDocumentPreSignedUrl}?${query}`,
  })
}

export const SaveCompanyDocumentFetcher = (baseURL: string, data: any, type?: string) => {
  return Axios.put(baseURL, data, {
    headers: {
      'Content-Type': type,
    },
  })
}

export const CompleteCompanyDocumentFetcher = (documentId: string) =>
  fetcher({
    baseURL: env.tenantUrl,
    url: `/carrier/documents/${documentId}`,
    method: 'PUT',
  })

export const GetUploadTenantDocumentPresignedUrlFetcher = ({ documentType }: { documentType: string }) => {
  return Axios.get(`${env.dataExtractionUrl}/documents/presignedUrl?documentType=${documentType}`, {})
}

export const GetUploadedTenantDocumentExtractedFetcher = ({ documentId }: { documentId: string }) => {
  return Axios.get(
    `${env.dataExtractionUrl}/documents/extract?documentId=${documentId}&documentType=TAX_LICENSE`,
    {}
  )
}
